import React, { useEffect, useState } from 'react'
import {
	Box,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	FormGroup,
	Paper,
	Switch,
	TextField,
	Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { dsColorNew } from '@palette'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminFieldsSx } from '../_shared/ui'

// The explanation under each section title, with the key words a shade darker
const sectionTextSx = { fontSize: 13, color: dsColorNew.textMuted, mb: 2, '& strong': { color: dsColorNew.textDark, fontWeight: 600 } }
const sectionSx = { borderTop: `1px solid ${dsColorNew.grayBorder}`, mt: 3, pt: 2.5 }

/**
 * D9 — CheckOut Config admin.
 *
 * Single-form admin for the tenant-wide thresholds that classify a
 * rental as Short vs Long and the per-day vs per-checkout prorating
 * behaviour. Used by the Short/Long filter and the future "Per Day /
 * Per CheckOut" toggle across Revenue, Fleet and Forecast.
 *
 * Defaults are seeded server-side; this form just shows the row and
 * patches subsets of fields with PATCH.
 */
export default function CheckoutConfigPage({ embedded = false }) {
	const [data, setData]       = useState(null)
	const [draft, setDraft]     = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving]   = useState(false)
	const [savedAt, setSavedAt] = useState(null)

	const fetchConfig = () => {
		setLoading(true); setError(null)
		fetch('/api/my-business/admin/checkout-config', { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then((row) => { setData(row); setDraft(row) })
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}

	useEffect(() => { fetchConfig() }, [])

	const fleetsEqual = (a, b) => {
		const A = [...(a || [])].map(Number).sort()
		const B = [...(b || [])].map(Number).sort()
		return A.length === B.length && A.every((x, i) => x === B[i])
	}

	const dirty = data && draft && (
		Number(draft.short_long_threshold_days) !== Number(data.short_long_threshold_days) ||
		Number(draft.prorrate_threshold_days)   !== Number(data.prorrate_threshold_days)   ||
		Boolean(draft.never_prorrate)           !== Boolean(data.never_prorrate)           ||
		!fleetsEqual(draft.fleet_ids_whitelist, data.fleet_ids_whitelist)
	)

	const save = () => {
		setSaving(true); setError(null)
		fetch('/api/my-business/admin/checkout-config', {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({
				short_long_threshold_days: Number(draft.short_long_threshold_days),
				prorrate_threshold_days:   Number(draft.prorrate_threshold_days),
				never_prorrate:            Boolean(draft.never_prorrate),
				fleet_ids_whitelist:       (draft.fleet_ids_whitelist || []).map(Number),
			}),
		})
			.then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(new Error(j.error || `HTTP ${r.status}`))))
			.then((row) => { setData(row); setDraft(row); setSavedAt(Date.now()) })
			.catch(e => setError(e.message))
			.finally(() => setSaving(false))
	}

	const resetDefaults = () => {
		if (!data?.defaults) return
		setDraft({
			...draft,
			short_long_threshold_days: data.defaults.short_long_threshold_days,
			prorrate_threshold_days:   data.defaults.prorrate_threshold_days,
			never_prorrate:            data.defaults.never_prorrate,
			fleet_ids_whitelist:       [...(data.defaults.fleet_ids_whitelist || [])],
		})
	}

	const justSaved = savedAt && Date.now() - savedAt < 2000

	const body = (
		<>
			<AdminProgress active={loading || saving}/>

			<Box sx={{ maxWidth: 720, mx: 'auto' }}>
				{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}
				{justSaved && <AppNote severity="success" sx={{ mb: 2 }}>Saved.</AppNote>}

				{loading && !data && <AppResultsSkeleton chart height={420}/>}

				{draft && (
					<Paper sx={{ p: 2.5, ...adminFieldsSx }}>
						<AppCardTitle icon="calendar-days" title="Rental duration" sx={{ mb: 0.75 }}/>
						<Typography sx={sectionTextSx}>
							Rentals with <strong>booked_days ≥ threshold</strong> are classified as <strong>Long</strong>.
							Anything below is <strong>Short</strong>. Used by the Short/Long filter across Revenue, Fleet and Forecast.
						</Typography>
						<TextField
							label="Short/Long threshold (days)"
							type="number"
							value={draft.short_long_threshold_days}
							onChange={(e) => setDraft({ ...draft, short_long_threshold_days: e.target.value })}
							inputProps={{ min: 1, max: 365 }}
							size="small"
							sx={{ width: 240 }}
							helperText={`Default: ${data?.defaults?.short_long_threshold_days ?? 30} days`}
						/>

						<Box sx={sectionSx}>
							<AppCardTitle icon="euro-sign" title="Revenue attribution" sx={{ mb: 0.75 }}/>
							<Typography sx={sectionTextSx}>
								Decides whether revenue lands fully on the checkout date (<strong>per-checkout</strong>)
								or is spread across each rental day (<strong>per-day</strong>) when the booking is long enough.
							</Typography>

							<Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
								<TextField
									label="Prorate threshold (days)"
									type="number"
									value={draft.prorrate_threshold_days}
									onChange={(e) => setDraft({ ...draft, prorrate_threshold_days: e.target.value })}
									inputProps={{ min: 1, max: 365 }}
									size="small"
									sx={{ width: 240 }}
									disabled={Boolean(draft.never_prorrate)}
									helperText={`Default: ${data?.defaults?.prorrate_threshold_days ?? 30} days`}
								/>

								<FormControlLabel
									control={
										<Switch checked={Boolean(draft.never_prorrate)}
										        onChange={(e) => setDraft({ ...draft, never_prorrate: e.target.checked })}/>
									}
									label="Never prorate"
								/>
							</Box>
						</Box>

						<Box sx={sectionSx}>
							<AppCardTitle icon="car" title="Fleet scope" sx={{ mb: 0.75 }}/>
							<Typography sx={sectionTextSx}>
								Only the fleets you tick are counted by
								<strong> fleet state / peak occupancy / KPIs</strong>.
								Leave every fleet unchecked to include ALL fleets (legacy
								behaviour). Ranteon exposes fleets like "Rent-a-car fleet",
								"VEHICULOS ANTERIORES" or "SIN ITV" — the operator
								usually wants only the operational fleet.
							</Typography>
							<FormGroup sx={{ '& .MuiFormControlLabel-label': { fontSize: 13.5, color: dsColorNew.textDark } }}>
								{(data?.available_fleets || []).map((fleet) => {
									const on = (draft.fleet_ids_whitelist || []).includes(fleet.id)
									return (
										<FormControlLabel key={fleet.id}
											control={
												<Checkbox checked={on}
													onChange={(e) => {
														const cur = new Set(draft.fleet_ids_whitelist || [])
														if (e.target.checked) cur.add(fleet.id)
														else                  cur.delete(fleet.id)
														setDraft({ ...draft,
															fleet_ids_whitelist: Array.from(cur).sort((a,b) => a-b),
														})
													}}/>
											}
											label={
												<Box display="flex" alignItems="center" gap={1}>
													<span>{fleet.name}</span>
													<Chip size="small" label={`${fleet.count} veh · id ${fleet.id}`}
														   sx={{ fontSize: 11.5, height: 20 }}/>
												</Box>
											}/>
									)
								})}
								{(!data?.available_fleets || !data.available_fleets.length) && (
									<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
										No fleets discovered yet — run <code>sync --entity vehicles</code>.
									</Typography>
								)}
							</FormGroup>
							{(draft.fleet_ids_whitelist || []).length === 0 && (
								<AppNote severity="info" sx={{ mt: 1.5 }}>
									Nothing ticked → no filter applied (counts every fleet).
								</AppNote>
							)}
						</Box>

						<Box sx={{ ...sectionSx, pt: 2 }}
						     display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
							<Button variant="contained" disabled={!dirty || saving} onClick={save}>
								Save
							</Button>
							<Button variant="outlined" disabled={saving} onClick={resetDefaults}>
								Restore defaults
							</Button>
							{data?.updated_at && (
								<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, ml: 'auto' }}>
									Last updated: {format(new Date(data.updated_at), 'dd MMM yyyy HH:mm')}
								</Typography>
							)}
						</Box>
					</Paper>
				)}
			</Box>
		</>
	)

	if (embedded) return body
	return (
		<MbPage title={MY_BUSINESS_ROUTES.CHECKOUT_ADMIN_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.CHECKOUT_ADMIN_SUBTITLE}>
			{body}
		</MbPage>
	)
}
